import React, { Component } from 'react';
import { ColumnProps } from 'primereact/column';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import WatchlistService from '../../../service/WatchlistService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomLoading from '../../custom/CustomLoading';
import CustomTable from '../../custom/CustomTable';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import {
  WatchlistBaseModel,
  WatchlistEntityRequest,
  WatchlistGeneralRequest,
  WatchlistIndividualRecord,
  WatchlistIndividualRequest,
  WatchlistNameModel,
  WatchlistNameType,
} from 'domain/watchlist/WatchlistDomains';
import { WatchlistType } from '../../../constant/Constants';
import { RootState } from '../../../redux/store';
import { changeWatchlist } from '../../../redux/actions/WatchlistActions';
import { WatchlistAlertDomains } from '../../../domain/watchlist/WatchlistAlertDomains';
import NavigationUtil from '../../../util/NavigationUtil';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import _ from 'lodash';
import LanguageUtil from '../../../util/LanguageUtil';

class WatchlistAlertRequestListComponent extends Component<Props, State> {
  public state = new State();

  private watchlistService: WatchlistService = ServiceContainer.getInstance().get(ServiceNames.WATCHLIST);

  public render() {
    const { searchResponse, requestLoading, rows, currentPage } = this.state;
    const startIndex = (currentPage - 1) * rows;
    const endIndex = startIndex + rows;
    const currentData = searchResponse.slice(startIndex, endIndex);

    if (requestLoading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col w-full h-full p-5'}>
        <div className={'w-full flex flex-col max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
          <CustomTable
            scrollable={true}
            scrollHeight={'flex'}
            selectionMode='single'
            onRowClick={(e) => this.onRowClick(e.data)}
            columns={this.getColumns()}
            value={currentData} />
        </div>
        <div className={'absolute bottom-0 w-full'}>
          <CustomPaginator
            rows={rows}
            totalRecords={searchResponse.length}
            rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
            currentPage={currentPage}
            fixed={true}
            onPageChange={this.onPageChange}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const { type, kycLogData } = this.props;
    this.setState({ requestLoading: true });
    let requestModel: WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest | undefined = kycLogData?.request;
    if (requestModel == undefined) return;
    requestModel.threshold = 70;
    try {
      const response: any = await this.watchlistService.searchMany(requestModel, type as WatchlistType);
      this.setState({
        searchResponse: response.sort((a: WatchlistBaseModel, b: WatchlistBaseModel) => b.score - a.score),
      });
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({
        requestLoading: false,
      });
    }
  }

  private onRowClick = (data: WatchlistBaseModel) => {
    const {
      router: { params },
      kycLogData,
    } = this.props;
    const previousSearchParams = new URLSearchParams(params.id);
    const type: string | null = previousSearchParams.get('type');
    const urlParams = new URLSearchParams();
    urlParams.set('type', type ?? '');
    urlParams.set('recordId', data.record.id ?? '');
    urlParams.set('score', (data.score ?? '').toString());
    urlParams.set('name', kycLogData?.request?.name || '');
    urlParams.set('threshold', kycLogData?.request?.threshold.toString() || '');
    urlParams.set('lists', kycLogData?.request?.lists.toString() || '');
    urlParams.set('email', kycLogData?.request?.email || '');
    urlParams.set('phone', kycLogData?.request?.phone || '');
    if (type == 'Individual') {
      urlParams.set('dateOfBirth', (kycLogData?.request as WatchlistIndividualRequest).dateOfBirth || '');
      urlParams.set('citizenship', (kycLogData?.request as WatchlistIndividualRequest).citizenship || '');
      urlParams.set('identification', (kycLogData?.request as WatchlistIndividualRequest).identification || '');
      urlParams.set('referenceId', (kycLogData?.request as WatchlistIndividualRequest).referenceId || '');
    } else if (type == 'Entity') {
      urlParams.set('dateOfRegistration', (kycLogData?.request as WatchlistEntityRequest).dateOfRegistration || '');
      urlParams.set('identifier', (kycLogData?.request as WatchlistEntityRequest).identifier || '');
      urlParams.set('referenceId', (kycLogData?.request as WatchlistEntityRequest).referenceId || '');
    } else if (type == 'General') {
      urlParams.set('date', (kycLogData?.request as WatchlistGeneralRequest).date || '');
      urlParams.set('id', (kycLogData?.request as WatchlistGeneralRequest).id || '');
      urlParams.set('referenceId', (kycLogData?.request as WatchlistGeneralRequest).referenceId || '');
    }
    window.open(`/${NavigationUtil.getTenant()}/watchlist/detail/${urlParams.toString()}`, '_blank', 'noopener noreferrer');
  };

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page });
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'score',
        header: LanguageUtil.get({ id: 'pages.alert.table.score', defaultMessage: 'Score' }),
        body: (record: WatchlistBaseModel) => {
          return (
            <div className={`flex flex-col justify-center items-center ${this.getScoreItem(record.score)} w-[40px] rounded-[4px] h-[22px]`}>{record.score}</div>
          );
        },
      },
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.alert.table.name', defaultMessage: 'Name' }),
        className: 'w-1/2',
        sortable: false,
        body: (record: WatchlistBaseModel) => {
          return (
            <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
              <div className={'w-8 h-8 flex items-center justify-center rounded-full bg-[#F4F3FF] mr-1'}>{this.getImage(record)}</div>
              {this.getName(record)}
            </div>
          );
        },
      },
      {
        field: 'category',
        header: LanguageUtil.get({ id: 'pages.alert.table.category', defaultMessage: 'Category' }),
        className: 'w-1/3',
        sortable: false,
        body: (record: WatchlistBaseModel) => {
          return (
            <div className={'flex items-center gap-x-2'}>
              {this.getCategory(record)?.map((value: string, index: number) => {
                return (
                  <span
                    key={index}
                    className={`flex text-xs shrink-0 2xl:text-md h-full justify-center px-[16px] py-[3px] items-center rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F]`}
                  >
                    {value}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.alert.table.createdAt', defaultMessage: 'Created At' }),
        alignHeader: 'left',
        columnKey: 'createdAt',
        className: 'w-1/3',
        body: (record: WatchlistBaseModel) => {
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={moment(record.record.createdAt).format('LLLL')} />
            </div>
          );
        },
      },
    ];
  };

  private getScoreItem = (score: number) => {
    if (score <= 25) {
      return 'bg-[#DBF8E5] text-[#3F9E2C]';
    } else if (score > 25 && score <= 50) {
      return 'bg-[#FCF1BB] text-[#C57500]';
    } else if (score > 50) {
      return 'bg-[#FFF1F3] text-[#D2332F]';
    }
  };

  private getCategory = (response: WatchlistBaseModel) => {
    if (response == undefined) return;
    const categories: any[] = [];
    response.record.records.forEach((response) => {
      categories.push(response.watchlistRecord.categories);
    });
    return _.uniqBy([].concat(...categories));
  };

  private getName = (response: WatchlistBaseModel): string => {
    if (response === undefined) return LanguageUtil.get({ id: 'pages.alert.nameUndefined', defaultMessage: 'Name Undefined' });
    const flattenedArray: any[] = response.record.records?.map((value) => {
      return value.watchlistRecord.names.map((name) => {
        return {
          ...name,
          watchlistId: value.watchlistId,
          source: value.watchlistRecord.source,
        };
      });
    });
    const namesData: WatchlistNameModel[] = [].concat(...flattenedArray);
    if (namesData === undefined || namesData.length === 0) return LanguageUtil.get({ id: 'pages.alert.nameUndefined', defaultMessage: 'Name Undefined' });
    const longestPrimaryData: WatchlistNameModel[] = namesData.filter((item: WatchlistNameModel) => item.nameType === WatchlistNameType.PRIMARY);
    if (longestPrimaryData.length === 0) return namesData[0].value;
    const data: WatchlistNameModel = longestPrimaryData.reduce((a, b) => (a.value.length > b.value.length ? a : b));
    return data.value;
  };

  private getImage = (record: WatchlistBaseModel) => {
    const { type } = this.props;
    if (record) {
      if (type == 'Individual') {
        const images = record.record.records.filter(
          (record) => (record.watchlistRecord as WatchlistIndividualRecord).images && (record.watchlistRecord as WatchlistIndividualRecord).images.length > 0,
        );
        if (images.length > 0) {
          return (
            <img
              src={(images[0].watchlistRecord as WatchlistIndividualRecord).images[0]}
              alt='-'
              className={'w-8 h-8 flex justify-center items-center rounded-[50%] bg-[#EFF2FE] object-cover'}
            />
          );
        }
        return <img src={'/icons/individual.svg'} alt='individual' />;
      } else {
        return <img src={'/icons/entity.svg'} alt='entity' />;
      }
    }
  };
}

interface Props extends FormicaProps, ReduxProps {
  kycLogData: WatchlistAlertDomains | undefined;
  type: string;
}

const mapStateToProps = (state: RootState) => ({
  watchlist: state.watchlist,
});
const mapDispatchToProps = { changeWatchlist };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  searchResponse: WatchlistBaseModel[] = [];
  requestLoading: boolean = false;
  rows: number = 10;
  currentPage: number = 1;
}

export default withFormica(connector(WatchlistAlertRequestListComponent));
